import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext({ theme: "light", resolved: "light", setTheme: () => {} });

const STORAGE_KEY = "pills-theme";

const resolveTheme = (pref) => {
    if (pref === "dark" || pref === "light") return pref;
    // "system"
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
        return "dark";
    }
    return "light";
};

const applyTheme = (resolved) => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (resolved === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    root.style.colorScheme = resolved;
};

export const ThemeProvider = ({ children }) => {
    const [theme, setThemeState] = useState(() => {
        if (typeof window === "undefined") return "system";
        return window.localStorage.getItem(STORAGE_KEY) || "system";
    });
    const [resolved, setResolved] = useState(() => resolveTheme(theme));

    useEffect(() => {
        const newResolved = resolveTheme(theme);
        setResolved(newResolved);
        applyTheme(newResolved);
        try { window.localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
    }, [theme]);

    // Watch system theme changes when in system mode
    useEffect(() => {
        if (theme !== "system" || typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = () => {
            const r = mq.matches ? "dark" : "light";
            setResolved(r);
            applyTheme(r);
        };
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, [theme]);

    const value = useMemo(() => ({
        theme,
        resolved,
        setTheme: setThemeState,
        toggle: () => setThemeState(resolved === "dark" ? "light" : "dark"),
    }), [theme, resolved]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
